import React from 'react';
import { motion } from 'framer-motion';
import Header from '../../layout/Header';
import Footer from '../../sections/Footer';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const { 
  FiMail, FiAlertTriangle, FiShield, FiArrowLeft,
  FiCheck, FiLock, FiGlobe, FiSmartphone, FiCreditCard,
  FiEye, FiUser, FiLink, FiPhoneCall, FiShoppingBag, FiBitcoin, FiCalendar
} = FiIcons;

const PhishingSchemesPage = () => {
  // Common phishing tactics
  const commonTactics = [
    "Sending emails that appear to be from legitimate companies requesting account verification",
    "Creating fake login pages that capture your credentials when entered",
    "Sending text messages with urgent requests to click suspicious links",
    "Claiming your account has been compromised and needs immediate action",
    "Using fear tactics about security threats or account suspension",
    "Making time-sensitive offers that are too good to be true",
    "Spoofing email addresses to appear as trusted contacts or organizations"
  ];

  // Protection tips
  const protectionTips = [
    "Never click links in unexpected emails or text messages",
    "Check the sender's email address carefully for subtle misspellings",
    "Hover over links to see the actual URL before clicking",
    "Go directly to the official website by typing the address in your browser",
    "Enable two-factor authentication on all important accounts",
    "Use a password manager to generate and store unique passwords",
    "Keep your devices and software updated with security patches"
  ];

  // Victim recovery steps
  const recoverySteps = [
    "Change passwords immediately for any compromised accounts",
    "Enable two-factor authentication where available",
    "Contact your bank or credit card company if financial information was shared",
    "Place a fraud alert on your credit reports",
    "Report the phishing attempt to the organization being impersonated",
    "Forward phishing emails to reportphishing@apwg.org and the FTC at reportfraud.ftc.gov",
    "Monitor your accounts closely for unauthorized activity"
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Phishing Scam Concept"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-purple-900/80 to-gray-900/90"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link 
              to="/types-of-scams" 
              className="inline-flex items-center text-gray-400 hover:text-[#ffc700] mb-6 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Back to All Scam Types
            </Link>

            <div className="flex items-center gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-4 rounded-full">
                <SafeIcon icon={FiMail} className="text-2xl text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                Phishing Schemes
              </h1>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
              <SafeIcon icon={FiAlertTriangle} className="text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-blue-400">High-Risk Scam Alert</h3>
                <p className="text-gray-300">
                  Phishing attacks have increased by 61% in the past year, with over 300,000 victims and $52 million in losses reported.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-16">
            {/* Overview Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  What Are Phishing Schemes?
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Phishing schemes are fraudulent attempts to steal sensitive information by disguising as trustworthy entities through electronic communications. Cybercriminals use deceptive emails, text messages, or websites that mimic legitimate organizations to trick individuals into revealing personal information such as passwords, credit card numbers, or social security numbers.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  These scams typically create a sense of urgency or fear to pressure victims into taking immediate action without carefully considering the request. Phishing remains one of the most common and effective cyberattack methods because it exploits human psychology rather than technical vulnerabilities.
                </p>
                
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-blue-500 mb-2">96%</div>
                    <p className="text-gray-400">of phishing attacks arrive by email</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-blue-500 mb-2">3.4B</div>
                    <p className="text-gray-400">phishing emails sent daily</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-blue-500 mb-2">32%</div>
                    <p className="text-gray-400">of data breaches involve phishing</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* How It Works Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  How Phishing Schemes Work
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="space-y-10">
                  {/* Step 1 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-blue-500/20 p-4 rounded-full border border-blue-500/30 flex-shrink-0">
                      <SafeIcon icon={FiUser} className="text-2xl text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Target Selection and Research</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Phishers often conduct research to identify potential targets. They may target specific organizations or cast a wide net with generic messages. Sophisticated phishers gather information about their targets from social media and other public sources to create more convincing messages.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-blue-500/20 p-4 rounded-full border border-blue-500/30 flex-shrink-0">
                      <SafeIcon icon={FiMail} className="text-2xl text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Creating Deceptive Communication</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Scammers craft emails, text messages, or social media messages that appear to come from legitimate sources like banks, popular websites, government agencies, or technical support services. They use official logos, similar email addresses, and professional language to create an appearance of legitimacy.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-blue-500/20 p-4 rounded-full border border-blue-500/30 flex-shrink-0">
                      <SafeIcon icon={FiLink} className="text-2xl text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Deploying the Bait</h3>
                      <p className="text-gray-300 leading-relaxed">
                        The message typically contains a compelling reason for you to take immediate action—your account has been compromised, you need to verify information, or there's a problem with a recent purchase. They include links to fake websites that closely mimic legitimate ones or attachments containing malware.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-blue-500/20 p-4 rounded-full border border-blue-500/30 flex-shrink-0">
                      <SafeIcon icon={FiCreditCard} className="text-2xl text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Information Theft</h3>
                      <p className="text-gray-300 leading-relaxed">
                        When victims click the malicious link, they're directed to a convincing but fake website where they're prompted to enter sensitive information like usernames, passwords, credit card details, or Social Security numbers. Once entered, this information is transmitted directly to the scammers, who can use it for identity theft, financial fraud, or to access your accounts.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10">
                  <h3 className="text-xl font-bold text-white mb-4">Common Tactics & Red Flags</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {commonTactics.map((tactic, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <SafeIcon icon={FiAlertTriangle} className="text-blue-500 mt-1 flex-shrink-0" />
                        <p className="text-gray-300">{tactic}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Protection Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  How to Protect Yourself
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {protectionTips.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                      <p className="text-gray-300">{tip}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiEye} className="text-blue-500" />
                    Linda's Quick Tips for Spotting Phishing
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    "Listen up! If you get an email saying your Amazon account is suspended and you need to click a link to fix it, stop right there! Amazon doesn't email you like that. And honey, if they're asking for your password or social security number in an email, that's not your bank—that's a scammer hoping you're not paying attention. When in doubt, pick up the phone and call the company directly using their official number."
                  </p>
                  <p className="text-[#ffc700] font-medium">- Linda Roberts</p>
                </div>
              </div>
            </motion.div>

            {/* What to Do If You're a Victim */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  What to Do If You're a Victim
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  If you suspect you've fallen victim to a phishing attack, quick action can help minimize the damage and protect your personal information and finances.
                </p>

                <div className="space-y-4 mb-8">
                  {recoverySteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-blue-500 to-cyan-500 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      <p className="text-gray-300">{step}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4">Important Resources</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">Anti-Phishing Working Group</p>
                        <a href="https://apwg.org" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">apwg.org</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">FTC's Identity Theft Site</p>
                        <a href="https://identitytheft.gov" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">identitytheft.gov</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiPhoneCall} className="text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">AVAH Outreach Group</p>
                        <Link to="/scam-assistance" className="text-blue-400 hover:underline">Get Free Victim Support</Link>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiMail} className="text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">Report Phishing Emails</p>
                        <a href="mailto:reportphishing@apwg.org" className="text-blue-400 hover:underline">reportphishing@apwg.org</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next Steps CTA */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-purple-900 to-gray-900 p-8 rounded-2xl border border-blue-500/30 relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-cyan-500/10 rounded-full translate-y-12 -translate-x-12"></div>

              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-4 text-center">
                  Continue Learning About Common Scams
                </h3>
                <p className="text-gray-300 text-center max-w-2xl mx-auto mb-8">
                  Knowledge is your best defense against scammers. Explore these related scam types to better protect yourself and your loved ones.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <Link
                    to="/scams/tech-support-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiSmartphone} className="text-2xl text-blue-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Tech Support Scams</p>
                  </Link>
                  <Link
                    to="/scams/impersonation-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiUser} className="text-2xl text-blue-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Impersonation Scams</p>
                  </Link>
                  <Link
                    to="/scams/online-shopping-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiShoppingBag} className="text-2xl text-blue-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Online Shopping Scams</p>
                  </Link>
                </div>

                <div className="mt-8 text-center">
                  <Link
                    to="/types-of-scams"
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3 shadow-lg"
                  >
                    <SafeIcon icon={FiShield} className="text-xl" />
                    View All Scam Types
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PhishingSchemesPage;